// src/hooks/useAccess.js
import { useState, useEffect, useCallback } from "react";
import axios from "axios";

/**
 * Unified Access Hook
 * - Loads current user from localStorage
 * - Loads permissions from the backend
 * - Filters by emp_roles
 * - Exposes: { currentUser, hasPermission, reloadPermissions }
 */
export default function useAccess() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  // 🧠 current user (from localStorage)
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("me") || "{}");
    } catch {
      return {};
    }
  });

  const [permissions, setPermissions] = useState([]);

  const parseEmpRoles = useCallback((roles) => {
    try {
      const parsed = typeof roles === "string" ? JSON.parse(roles) : roles;
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
      return [];
    }
  }, []);

  // Keep user synced if "me" changes
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "me") {
        try {
          setCurrentUser(JSON.parse(e.newValue || "{}"));
        } catch {
          setCurrentUser({});
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Load permissions from API
  const loadPermissions = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${BASE_URL}/api/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const roleIds = parseEmpRoles(currentUser?.emp_roles);
      const filtered = Array.isArray(roleIds)
        ? data.filter((p) => roleIds.includes(Number(p.role_id)))
        : [];
      setPermissions(filtered);
    } catch (err) {
      console.error("❌ Failed to load permissions:", err);
    }
  }, [BASE_URL, token, currentUser, parseEmpRoles]);

  useEffect(() => {
    loadPermissions();
    const refresh = () => loadPermissions();
    window.addEventListener("permissionsUpdated", refresh);
    return () => window.removeEventListener("permissionsUpdated", refresh);
  }, [loadPermissions]);

  // ✅ Permission checker
  const hasPermission = useCallback(
    (pageName, resourceName, action) =>
      permissions.some(
        (p) =>
          p.page_name === pageName &&
          p.resource_name === resourceName &&
          p.action === action
      ),
    [permissions]
  );

  return {
    currentUser,
    permissions,
    hasPermission,
    reloadPermissions: loadPermissions,
  };
}
